import { languageRegistry as registry } from './languageRegistry.generated.ts'

export type LanguageTag = string & { readonly __languageTag: unique symbol }

const invalid = (): never => { throw new Error('invalid language tag') }

/** RFC 5646, using the pinned IANA registry; no likely-subtag/CLDR inference. */
export function canonicalLanguageTag(raw: string): LanguageTag {
  if (typeof raw !== 'string' || raw.length < 1 || raw.length > 255 || !/^[A-Za-z0-9]+(-[A-Za-z0-9]+)*$/.test(raw)) return invalid()
  const s = raw.toLowerCase()
  for (const kind of ['grandfathered', 'redundant']) {
    const entries = registry[kind]!
    const entry = Object.hasOwn(entries, s) ? entries[s] : undefined
    if (entry?.preferred) return canonicalLanguageTag(entry.preferred)
    if (entry && kind === 'grandfathered') return s as LanguageTag
  }
  const p = s.split('-')
  const first = p[0]!
  if (first === 'x') {
    if (p.length < 2 || p.slice(1).some(v => v.length > 8)) return invalid()
    return s as LanguageTag
  }
  const get = (kind: string, key: string) => {
    const entries = registry[kind]
    if (!entries || !Object.hasOwn(entries, key)) return invalid()
    return entries[key]!
  }
  const base = get('language', first)
  let lang = base.preferred ?? first
  let i = 1
  const current = () => p[i] ?? ''
  if (first.length <= 3 && i < p.length && /^[a-z]{3}$/.test(current())) {
    const ext = get('extlang', current())
    if (ext.prefixes?.length !== 1 || ext.prefixes[0] !== first || !ext.preferred) return invalid()
    lang = ext.preferred
    i++
  }
  const parts = [lang]
  if (i < p.length && /^[a-z]{4}$/.test(current())) {
    const script = get('script', current())
    const v = script.preferred ?? current()
    if (get('language', lang).suppress !== v) parts.push(v.charAt(0).toUpperCase() + v.slice(1))
    i++
  }
  if (i < p.length && /^([a-z]{2}|[0-9]{3})$/.test(current())) {
    const region = get('region', current())
    parts.push((region.preferred ?? current()).toUpperCase())
    i++
  }
  const variants = new Set<string>()
  while (i < p.length && /^([a-z0-9]{5,8}|[0-9][a-z0-9]{3})$/.test(current())) {
    const entry = get('variant', current())
    const v = entry.preferred ?? current()
    if (variants.has(v)) return invalid()
    variants.add(v)
    parts.push(v)
    i++
  }
  const extensions = new Map<string, string>()
  while (i < p.length && current().length === 1 && current() !== 'x') {
    const key = current()
    if (extensions.has(key)) return invalid()
    const start = i++
    while (i < p.length && current().length >= 2 && current().length <= 8) i++
    if (i === start + 1) return invalid()
    extensions.set(key, p.slice(start, i).join('-'))
  }
  for (const key of [...extensions.keys()].sort()) parts.push(extensions.get(key)!)
  if (i < p.length && current() === 'x') {
    const start = i++
    while (i < p.length && current().length <= 8) i++
    if (i === start + 1) return invalid()
    parts.push(p.slice(start, i).join('-'))
  }
  if (i !== p.length) return invalid()
  const canonical = parts.join('-')
  if (canonical.length > 255) return invalid()
  return canonical as LanguageTag
}

/** RFC 4647 structural lookup; no macrolanguage or likely-region parents. */
export function parentLanguages(tag: LanguageTag): LanguageTag[] {
  const parts = tag.split('-')
  const result: LanguageTag[] = []
  const seen = new Set<LanguageTag>([tag])
  while (parts.length > 1) {
    parts.pop()
    if (parts.at(-1)?.length === 1) parts.pop()
    if (!parts.length) break
    try {
      const parent = canonicalLanguageTag(parts.join('-'))
      if (!seen.has(parent)) { result.push(parent); seen.add(parent) }
    } catch { /* A truncated extension/grandfathered prefix need not be valid. */ }
  }
  return result
}
